/**
 * Where the customer was heading before they were sent to sign in, so they land there afterwards rather
 * than on the dashboard.
 *
 * `sessionStorage`, matching what Okta's `setOriginalUri` did: the deep link is meaningful only to the tab
 * that stored it, and it must not survive the browser being closed.
 */
const DEEP_LINK_KEY = 'fluro_deep_link'

export function rememberDeepLink(relativeUri: string): void {
  try {
    window.sessionStorage.setItem(DEEP_LINK_KEY, relativeUri)
  } catch {
    // Storage unavailable; the customer lands on the dashboard instead of the deep link.
  }
}

/** Reads the remembered deep link and clears it, so a later sign-in does not reuse a stale one. */
export function takeDeepLink(): string | null {
  try {
    const uri = window.sessionStorage.getItem(DEEP_LINK_KEY)
    window.sessionStorage.removeItem(DEEP_LINK_KEY)
    return uri
  } catch {
    return null
  }
}

/**
 * The path + query + hash of [href] when it belongs to [origin], for storing and for `navigate`.
 *
 * Replaces `toRelativeUrl` from `@okta/okta-auth-js` so the auth flow no longer depends on Okta. An href
 * from another origin returns '/' rather than being trusted — a stored deep link is later navigated to, and
 * an absolute URL there would be an open redirect.
 */
export function relativeUrlOf(href: string, origin: string): string {
  try {
    const url = new URL(href, origin)
    return url.origin === origin ? `${url.pathname}${url.search}${url.hash}` : '/'
  } catch {
    return '/'
  }
}
